import { useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import BrandLogo from '@/components/BrandLogo';
import {
  LayoutDashboard, FileText, Image, BookOpen, User, Mail,
  Phone, Settings, LogOut, Menu, X, ChevronRight, Users, CreditCard, DollarSign, CalendarCheck
} from 'lucide-react';

export default function AdminLayout() {
  const { logout, hasPermission, isSuperAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const ADMIN_LINKS = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true, show: true },
    { path: '/admin/content', label: 'Site Content', icon: FileText, show: hasPermission('content') },
    { path: '/admin/founder', label: 'Founder', icon: User, show: hasPermission('founder') },
    { path: '/admin/gallery', label: 'Gallery', icon: Image, show: hasPermission('gallery') },
    { path: '/admin/blog', label: 'Blog Posts', icon: BookOpen, show: hasPermission('blog') },
    { path: '/admin/contact', label: 'Contact Info', icon: Phone, show: hasPermission('contact') },
    { path: '/admin/messages', label: 'Messages', icon: Mail, show: hasPermission('messages') },
    { path: '/admin/activity-registrations', label: 'Activity Registrations', icon: CalendarCheck, show: hasPermission('activity_registrations') },
    { path: '/admin/donations', label: 'Donations', icon: DollarSign, show: hasPermission('donations') },
    { path: '/admin/stripe', label: 'Stripe Settings', icon: CreditCard, show: isSuperAdmin() },
    { path: '/admin/users', label: 'User Management', icon: Users, show: isSuperAdmin() },
    { path: '/admin/footer-settings', label: 'Footer Settings', icon: FileText, show: isSuperAdmin() },
    { path: '/admin/settings', label: 'Settings', icon: Settings, show: true },
  ].filter(link => link.show);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const isActive = (link) => {
    if (link.exact) return location.pathname === link.path;
    return location.pathname.startsWith(link.path);
  };

  return (
    <div className="mscm-admin-shell min-h-screen overflow-x-hidden">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white/95 border-b border-[#E2E8F0] h-14 flex items-center px-4 shadow-[0_10px_28px_rgba(11,31,77,0.06)] backdrop-blur-xl">
        <button
          data-testid="admin-sidebar-toggle"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="inline-flex h-10 w-10 items-center justify-center rounded-full text-[#475569] transition-colors hover:bg-[#EBF2FA] hover:text-[#1E4FA1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1E4FA1]/30"
          aria-label={sidebarOpen ? 'Close admin navigation' : 'Open admin navigation'}
          aria-expanded={sidebarOpen}
        >
          {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
        <span className="ml-3 font-semibold text-[#1E4FA1] text-sm">Admin Dashboard</span>
      </div>

      {/* Sidebar */}
      <aside
        data-testid="admin-sidebar"
        className={`mscm-admin-sidebar fixed top-0 left-0 bottom-0 w-64 border-r border-[#E2E8F0] z-40 transform transition-transform duration-200 lg:translate-x-0 lg:visible ${
          sidebarOpen ? 'translate-x-0 visible' : '-translate-x-full invisible'
        }`}
      >
        <div className="p-5 border-b border-[#E2E8F0]">
          <Link to="/" className="flex items-center gap-2" data-testid="admin-home-link">
            <BrandLogo
              src="/logo.png"
              alt="Million Stars Community Ministry"
              variant="admin"
              frameClassName="shrink-0"
              imageClassName="h-8 md:h-8"
            />
            <div>
              <div className="text-sm font-semibold text-[#0F172A]">MSCM Admin</div>
              <div className="text-xs text-[#94A3B8]">Management Panel</div>
            </div>
          </Link>
        </div>

        <nav className="p-3 flex flex-col gap-0.5 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 140px)' }}>
          {ADMIN_LINKS.map((link) => {
            const Icon = link.icon;
            const active = isActive(link);
            return (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`admin-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => setSidebarOpen(false)}
                className={`mscm-admin-nav-link flex items-center gap-3 px-3 py-2.5 rounded-[8px] text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1E4FA1]/30 ${
                  active
                    ? 'mscm-admin-nav-link-active'
                    : 'text-[#475569] hover:bg-[#EBF2FA] hover:text-[#1E4FA1]'
                }`}
              >
                <Icon size={18} />
                <span>{link.label}</span>
                {active && <ChevronRight size={14} className="ml-auto" />}
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-[#E2E8F0]">
          <button
            data-testid="admin-logout-btn"
            onClick={handleLogout}
            className="flex min-h-11 w-full items-center gap-3 rounded-[8px] px-3 py-2.5 text-sm font-semibold text-red-500 transition-colors hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-200"
          >
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-[#0B1F4D]/30 z-30 backdrop-blur-[2px]"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Main Content */}
      <main className="min-w-0 lg:ml-64 min-h-screen pt-14 lg:pt-0">
        <div className="p-4 md:p-8 lg:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
